import { useApp, useTranslation, useUserInteractions } from "@next-platform/client-common-core";
import { IconVariants, Input, Size, useMedia } from "@next-platform/client-common-spicy-ui";
import { useEffect, useRef, useState } from "react";
import { useHits, useSearchBox } from "react-instantsearch";
import { useDebounceValue } from "usehooks-ts";

interface SharedChatbotsSearchBarProps {
  queryBy: string;
  setQueryBy: (value: string) => void;
  defaultQueryBy: string;
  setFoundNegativeKeyword: (value: boolean) => void;
}

export default function SharedChatbotsSearchBar({ queryBy, setQueryBy, defaultQueryBy, setFoundNegativeKeyword }: SharedChatbotsSearchBarProps) {
  const { isMobile } = useMedia();
  const { results } = useHits();
  const { refine } = useSearchBox();
  const { setIsSearchInProgress } = useUserInteractions();
  const { negativeKeywords } = useApp();
  const { t } = useTranslation();

  const isMounted = useRef(false);
  const [searchedQuery, setSearchQuery] = useState(results?.query);
  const [debouncedSearchQuery] = useDebounceValue(searchedQuery, 300);

  useEffect(() => {
    if (results?.query && !isMounted.current) {
      if (results?.query !== "*") {
        setSearchQuery(results?.query);
      }
      isMounted.current = true;
    }
  }, [results?.query]);

  useEffect(() => {
    if (isMounted.current) {
      setIsSearchInProgress(!!(debouncedSearchQuery && debouncedSearchQuery?.length > 0));
      if (searchedQuery !== results?.query) {
        refine(debouncedSearchQuery as string);
      }
    }
  }, [debouncedSearchQuery, refine, searchedQuery, results?.query, setIsSearchInProgress]);

  const handleInputChange = (value: string) => {
    setSearchQuery(value);

    // Handle @username search
    if (value.startsWith("@")) {
      if (queryBy === defaultQueryBy) {
        setQueryBy("creator_username");
      }
    } else {
      if (queryBy !== defaultQueryBy) {
        setQueryBy(defaultQueryBy);
      }

      // Check for negative keywords
      if (negativeKeywords) {
        const hasNegativeKeyword = negativeKeywords.some((entry) => entry.alternatives.includes(value.toLowerCase()));
        setFoundNegativeKeyword(hasNegativeKeyword);
      }
    }
  };

  return (
    <Input
      value={searchedQuery}
      iconStart={IconVariants.SEARCH}
      onClear={() => setSearchQuery("")}
      isClearable={!!searchedQuery}
      iconEnd={searchedQuery ? IconVariants.CLOSE : undefined}
      placeholder={t("search:placeholder")}
      onChange={handleInputChange}
      size={Size.SMALL}
      width={isMobile ? "100%" : 400}
    />
  );
}
